import { useContext, useId } from "react";
import BaseScene from "./BaseScene";
import { track } from "../track";
import type { BaseStyle } from "../types";
import type { Words } from "./copy";
import { cleanMessage, messageLength, messageLimit, messageProblem, takesBase, type MaxChars } from "./christmas";
import { PageContext, type BaseChoice } from "./page";

/** The Christmas base on the order sheet: a figure standing on a red drum
 *  with snow on top, and a one-line message raised in white around its front.
 *  Free, so nothing about a price moves.
 *
 *  Chosen here, on the first step of the sheet, under the sizes, because the
 *  size decides how many characters fit (the seed's christmas.base.maxChars,
 *  sent with the page's read): a message that fits at 8 cm can be too long at
 *  6 cm. It is never cut to fit. The sheet says how many fit at this size and
 *  offers the next size up where the message would fit there, and Continue
 *  stays off until it fits (baseBlocks); /api/checkout asks exactly the same
 *  of it (preview/christmas.ts is the server's rules copied), and refuses
 *  rather than cut.
 *
 *  The picture beside it is drawn here, never by the AI: their own drawing,
 *  standing on a base drawn in SVG, with their words set in the base's own
 *  font (Baloo 2 ExtraBold, the one the 3D pipeline raises the letters in,
 *  cut down to Latin in public/fonts by scripts/subset-base-font.mjs) along
 *  the front of it. Fetched on the first toggle, not before, and a bold
 *  system face stands in until it arrives or if it never does. It is an
 *  illustration and says so, with how the letters will really be made.
 *
 *  Only for the figures (takesBase: minime and duo), only where the page
 *  offers it (PageInfo.baseLimits) and only when paying on the page: Etsy's
 *  listing has no base, and the Etsy steps say so (baseNotOnEtsy). */

/** What a line is sent with when its base is on and can be made: the base,
 *  and its message as it will be raised, cleaned (cleanMessage). Null for its
 *  plain base, which is also what a base the page no longer offers, or a size
 *  that takes none, comes to: the request is then the one it always was. */
export function chosenBase(
  choice: BaseChoice | undefined,
  kind: string | undefined,
  size: string,
  limits: MaxChars | null | undefined
): { base: BaseStyle; message: string } | null {
  if (!choice?.on || !limits || !kind || !takesBase(kind) || !messageLimit(kind, size, limits)) return null;
  return { base: "christmas", message: cleanMessage(choice.message) };
}

/** Whether the base they chose keeps Continue off: its message holds a
 *  character the base cannot print, or more than fit at this size. */
export function baseBlocks(
  choice: BaseChoice | undefined,
  kind: string | undefined,
  size: string,
  limits: MaxChars | null | undefined
): boolean {
  const chosen = chosenBase(choice, kind, size, limits);
  return !!chosen && !!kind && messageProblem(chosen.message, kind, size, limits ?? {}) !== null;
}

/** "8 cm" as a number, to tell a bigger size from a smaller one. */
function cm(size: string): number {
  const n = Number.parseFloat(size);
  return Number.isFinite(n) ? n : 0;
}

export default function ChristmasBase({
  kind,
  size,
  sizes,
  choice,
  onChange,
  onSize,
  drawingUrl,
  compact,
  words: t,
}: {
  /** The kind the base is asked by (baseKindOf): minime, duo or pet_figure. */
  kind: string;
  /** The size picked, whose limit the message is counted against. */
  size: string;
  /** Every size offered, for the next one up a long message would fit. */
  sizes: string[];
  choice: BaseChoice | undefined;
  onChange: (choice: BaseChoice) => void;
  /** Pick another size, from "Pick 8 cm" under a message too long for this
   *  one. Left out, the sentence says it without a button. */
  onSize?: (size: string) => void;
  /** Their drawing, standing on the base in the mock-up. */
  drawingUrl?: string | null;
  /** One line of a list of pieces ("Order them together", a card on a page
   *  of several): the toggle without its sentence, and a smaller mock-up
   *  beside the field. */
  compact?: boolean;
  words: Words;
}) {
  const page = useContext(PageContext);
  // Ids for the mock-up's gradient, mask and arc, and the field's label:
  // one set per toggle on the page, in the characters an SVG reference takes.
  const id = `xb${useId().replace(/[^A-Za-z0-9_-]/g, "")}`;
  const limits = page?.baseLimits ?? null;
  const on = !!choice?.on;
  const limit = limits ? messageLimit(kind, size, limits) : 0;
  if (!limits || !takesBase(kind) || !limit) return null;

  const message = choice?.message ?? "";
  const count = messageLength(message);
  const problem = on ? messageProblem(message, kind, size, limits) : null;
  // The next size up that this message would fit, where there is one.
  const bigger =
    problem === "too-long"
      ? sizes
          .filter((s) => cm(s) > cm(size) && messageLimit(kind, s, limits) >= count)
          .sort((a, b) => cm(a) - cm(b))[0]
      : undefined;
  // The field takes far more than any size does, not this size's: a phone
  // pasting a message cuts it at maxLength without a word, and nothing here is
  // ever cut. The browser counts maxLength in UTF-16 units of the raw text,
  // before it is composed and its spaces folded, so a message pasted from a
  // Mac's notes (each accent a character of its own) or with doubled spaces
  // would be cut short of what it counts as once cleaned. Twice the biggest
  // size's count leaves room for both, and only keeps an essay out; too long
  // for this size is said under it, and keeps Continue off.
  const widest = 2 * Math.max(limit, ...sizes.map((s) => messageLimit(kind, s, limits)));
  const chinese = page?.language === "zh" || page?.language === "zh-hans";
  const placeholder = t.t("basePlaceholder");

  const toggle = () => {
    const next = !on;
    track("base.toggled", { on: next, kind, size });
    onChange({ on: next, message });
  };

  return (
    <div className={compact ? "xbase xbase-compact" : "xbase"}>
      <button type="button" className="xbase-toggle" aria-pressed={on} onClick={toggle}>
        <span className="xbase-box" aria-hidden="true" />
        <span className="xbase-text">
          <strong className="small">{t.t("baseToggle")}</strong>
          {!compact && <span className="tiny muted">{t.t("baseHint")}</span>}
        </span>
      </button>
      {on && (
        <div className="xbase-body">
          <BaseScene
            drawingUrl={drawingUrl ?? null}
            style="christmas"
            text={cleanMessage(message)}
            placeholder={placeholder}
            label={`${t.t("baseMockNote")} ${cleanMessage(message) || placeholder}`}
          />
          <div className="xbase-fields">
            <label className="xbase-label tiny" htmlFor={`${id}-message`}>
              {t.t("baseMessage")}
            </label>
            <input
              id={`${id}-message`}
              className="field xbase-input"
              type="text"
              value={message}
              maxLength={widest}
              placeholder={placeholder}
              autoComplete="off"
              enterKeyHint="done"
              aria-invalid={problem ? true : undefined}
              aria-describedby={`${id}-count${problem ? ` ${id}-why` : ""}`}
              onChange={(e) => onChange({ on: true, message: e.target.value })}
              onBlur={() => {
                if (count) track("base.typed", { chars: count, size, kind, fits: !problem });
              }}
            />
            <p
              id={`${id}-count`}
              className={problem === "too-long" ? "tiny xbase-count xbase-over" : "tiny muted xbase-count"}
              aria-live="polite"
            >
              {t.t("baseCount", { n: String(count), max: String(limit) })}
            </p>
          </div>
          {problem === "too-long" ? (
            <p id={`${id}-why`} className="tiny xbase-why">
              {t.t(bigger ? "baseTooLong" : "baseTooLongMax", { max: String(limit), size })}
              {bigger && onSize && (
                <>
                  {" "}
                  <button type="button" className="btn btn-ghost btn-sm xbase-bigger" onClick={() => onSize(bigger)}>
                    {t.t("baseBigger", { size: bigger })}
                  </button>
                </>
              )}
            </p>
          ) : problem === "charset" ? (
            <p id={`${id}-why`} className="tiny xbase-why">
              {t.t("baseCharset")}
            </p>
          ) : (
            // Said before they type, on a page in Chinese, where the first
            // thing anyone would type is what the base cannot print.
            chinese && <p className="tiny muted xbase-note">{t.t("baseCharset")}</p>
          )}
          <p className="tiny muted xbase-note">{t.t("baseMockNote")}</p>
        </div>
      )}
    </div>
  );
}
